import { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Sparkles } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PURPOSES = [
  ["provider", "model", "Business assistant & chat", "Answers your questions and writes messages"],
  ["reasoning_provider", "reasoning_model", "Auditing & forecasts", "Payroll audit, HR insights, forecasts"],
  ["vision_provider", "vision_model", "Vision (selfie / face check)", "Kiosk face match and photo checks"],
];

export const AiSettings = ({ settings, set, saveSection }) => {
  const [catalogue, setCatalogue] = useState(null);
  const [testing, setTesting] = useState("");
  const [results, setResults] = useState({});

  const load = useCallback(() => {
    api.get("/ai/models").then(({ data }) => setCatalogue(data))
      .catch((e) => logError("ai models", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async (providerKey, modelKey) => {
    const provider = settings.ai[providerKey];
    const model = settings.ai[modelKey];
    setTesting(modelKey);
    try {
      const { data } = await api.post("/ai/test", {
        provider, model,
        prompt: "In one short sentence, confirm you can help a manufacturing ERP owner.",
      });
      setResults({ ...results, [modelKey]: data });
      toast.success(`${data.provider}/${data.model} replied in ${data.seconds}s`);
    } catch (e) {
      setResults({ ...results, [modelKey]: { error: apiError(e) } });
      toast.error(apiError(e));
    } finally {
      setTesting("");
    }
  };

  if (!catalogue) {
    return (
      <div className="surface mt-4 flex h-40 items-center justify-center" data-testid="settings-ai">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const providerFor = (key) => catalogue.providers.find((p) => p.value === settings.ai[key]);

  return (
    <div className="surface mt-4 space-y-4 p-5" data-testid="settings-ai">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="gap-1 text-xs" data-testid="ai-key-source">
          <Sparkles className="h-3 w-3" /> {catalogue.key_source}
        </Badge>
        <span className="text-xs text-muted-foreground">
          ChatGPT, Claude and Gemini all run on your Emergent key — no separate API key needed.
        </span>
      </div>

      {PURPOSES.map(([providerKey, modelKey, title, hint]) => {
        const provider = providerFor(providerKey);
        const isVision = modelKey === "vision_model";
        const models = provider
          ? (isVision && provider.vision_models.length ? provider.vision_models : provider.models)
          : [];
        const result = results[modelKey];
        return (
          <div key={modelKey} className="rounded-lg border border-border p-4"
            data-testid={`ai-purpose-${modelKey}`}>
            <div className="text-sm font-medium">{title}</div>
            <p className="text-xs text-muted-foreground">{hint}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Field label="Provider">
                <Select value={settings.ai[providerKey] || ""}
                  onValueChange={(v) => {
                    set("ai", providerKey, v);
                    const next = catalogue.providers.find((p) => p.value === v);
                    if (next) {
                      set("ai", modelKey, isVision && next.vision_models.length
                        ? next.vision_models[0] : next.recommended);
                    }
                  }}>
                  <SelectTrigger data-testid={`ai-provider-${providerKey}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catalogue.providers
                      .filter((p) => !isVision || p.vision_models.length)
                      .map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Model">
                <Select value={settings.ai[modelKey] || ""}
                  onValueChange={(v) => set("ai", modelKey, v)}>
                  <SelectTrigger data-testid={`ai-model-${modelKey}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}{m === provider?.recommended ? " — recommended" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-end">
                <Button size="sm" variant="outline" className="gap-1.5"
                  disabled={testing === modelKey || isVision}
                  data-testid={`ai-test-${modelKey}`}
                  onClick={() => runTest(providerKey, modelKey)}>
                  {testing === modelKey ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {isVision ? "Tested from the kiosk" : "Test this model"}
                </Button>
              </div>
            </div>
            {result && (
              <p className={`mt-2 text-xs ${result.error ? "text-destructive" : "text-muted-foreground"}`}
                data-testid={`ai-test-result-${modelKey}`}>
                {result.error ? result.error : `“${result.reply}” — ${result.model}, ${result.seconds}s`}
              </p>
            )}
          </div>
        );
      })}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="High confidence threshold %">
          <Input
            data-testid="ai-high-threshold-input"
            value={settings.ai.high_confidence}
            onChange={(e) => set("ai", "high_confidence", Number(e.target.value))}
          />
        </Field>
        <Field label="Medium confidence threshold %">
          <Input
            value={settings.ai.medium_confidence}
            onChange={(e) => set("ai", "medium_confidence", Number(e.target.value))}
          />
        </Field>
      </div>

      <Button className="gap-1.5" data-testid="save-ai-button" onClick={() => saveSection("ai")}>
        <Save className="h-4 w-4" /> Save AI settings
      </Button>
      <p className="text-xs text-muted-foreground">
        AI classifies, extracts and recommends. Stock, purchase, payroll and accounting entries are
        only created after human approval, and all money maths is deterministic.
      </p>
    </div>
  );
};
